import {
  Anchor,
  Box,
  Container,
  Grid,
  Group,
  Image,
  Stack,
  Text,
  useComputedColorScheme,
  useMantineTheme,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useLanguageAwareRouting } from '@/hooks/useLanguageAwareRouting';

export function Footer() {
  const { t } = useTranslation();
  const { createLanguageURL } = useLanguageAwareRouting();
  const theme = useMantineTheme();
  const computedColorScheme = useComputedColorScheme('light');
  const isDark = computedColorScheme === 'dark';

  const surfaceColor = isDark ? theme.colors.dark[7] : '#ffffff';
  const borderColor = isDark ? theme.colors.dark[5] : '#e4e8f0';

  return (
    <Box
      component="footer"
      mt={64}
      style={{
        backgroundColor: surfaceColor,
        borderTop: `1px solid ${borderColor}`,
      }}
    >
      <Container size="xl" px="md" py={48}>
        <Grid gutter="xl">
          <Grid.Col span={{ base: 12, md: 4 }}>
            <Stack gap="sm">
              <Anchor
                component={Link}
                to={createLanguageURL('/')}
                style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 8 }}
              >
                <Image src="/logo.svg" alt={t('brand.logoAlt')} w={28} h={28} />
                <Text
                  fw={700}
                  size="lg"
                  style={{
                    letterSpacing: '-0.01em',
                    color: isDark ? '#ffffff' : '#0A192F',
                  }}
                >
                  {t('brand.name')}
                  {t('brand.nameSecond')}
                </Text>
              </Anchor>
              <Text size="sm" c="dimmed" maw={320}>
                {t('welcome.tagline')}
              </Text>
            </Stack>
          </Grid.Col>

          <Grid.Col span={{ base: 6, md: 4 }}>
            <Stack gap="xs">
              <Text fw={600} size="sm" mb={4}>
                {t('glob_footer.product')}
              </Text>
              <Anchor component={Link} to={createLanguageURL('/pricing')} size="sm" c="dimmed">
                {t('glob_navigation.pricing')}
              </Anchor>
              <Anchor component={Link} to={createLanguageURL('/integrations')} size="sm" c="dimmed">
                {t('glob_navigation.integrations')}
              </Anchor>
              <Anchor component={Link} to={createLanguageURL('/docs')} size="sm" c="dimmed">
                {t('glob_navigation.docs')}
              </Anchor>
              <Anchor component={Link} to={createLanguageURL('/news')} size="sm" c="dimmed">
                {t('glob_navigation.news')}
              </Anchor>
            </Stack>
          </Grid.Col>

          <Grid.Col span={{ base: 6, md: 4 }}>
            <Stack gap="xs">
              <Text fw={600} size="sm" mb={4}>
                {t('glob_footer.account')}
              </Text>
              <Anchor component={Link} to={createLanguageURL('/login')} size="sm" c="dimmed">
                {t('glob_navigation.login')}
              </Anchor>
              <Anchor component={Link} to={createLanguageURL('/signup')} size="sm" c="dimmed">
                {t('glob_navigation.getStarted')}
              </Anchor>
              <Anchor href={`#${createLanguageURL('/privacy')}`} size="sm" c="dimmed">
                {t('privacy.title')}
              </Anchor>
              <Anchor href={`#${createLanguageURL('/terms')}`} size="sm" c="dimmed">
                {t('glob_navigation.terms')}
              </Anchor>
            </Stack>
          </Grid.Col>
        </Grid>

        <Group
          justify="space-between"
          mt={48}
          pt="lg"
          style={{ borderTop: `1px solid ${borderColor}` }}
          wrap="wrap"
          gap="md"
        >
          <Text size="xs" c="dimmed">
            {t('privacy.gdprCompliant')}
          </Text>
          <Group gap={6} align="center">
            <Text size="xs" c="dimmed">
              By
            </Text>
            <Anchor
              href="https://gofranz.com/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: 'flex', alignItems: 'center' }}
            >
              <img
                src={isDark ? '/gofranz_white.svg' : '/gofranz.svg'}
                alt="GoFranz - German Cloud Infrastructure"
                height={20}
                width={60}
                style={{
                  opacity: 0.7,
                  transition: 'opacity 0.2s',
                  filter: isDark ? 'none' : 'contrast(1.2) brightness(0.8)',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.7')}
              />
            </Anchor>
          </Group>
        </Group>
      </Container>
    </Box>
  );
}
